import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller } from 'react-hook-form';
import * as z from 'zod';
import { Send, CheckCircle, Mail, MapPin, Clock } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';

// Define the form schema
const formSchema = z.object({
  firstName: z.string().min(2, {
    message: 'First name must be at least 2 characters.',
  }),
  lastName: z.string().min(2, {
    message: 'Last name must be at least 2 characters.',
  }),
  company: z.string().optional(),
  email: z.string().email({
    message: 'Please enter a valid email address.',
  }),
  phone: z.string().optional(),
  message: z.string().min(10, {
    message: 'Message must be at least 10 characters.',
  }),
  industry: z.string().optional(),
  companySize: z.string().optional(),
  howHeard: z.string().optional(),
  interest: z.array(z.string()),
  urgent: z.boolean(),
  marketingConsent: z.boolean(),
});

// Define form values type
type FormValues = z.infer<typeof formSchema>;

// List of industries for the dropdown
const industries = [
  { value: 'technology', label: 'Technology' },
  { value: 'finance', label: 'Finance & Banking' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'education', label: 'Education' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'retail', label: 'Retail & E-commerce' },
  { value: 'professional', label: 'Professional Services' },
  { value: 'government', label: 'Government' },
  { value: 'nonprofit', label: 'Non-profit' },
  { value: 'other', label: 'Other' },
];

// Company size options
const companySizes = [
  { value: '1-10', label: '1-10 employees' },
  { value: '11-50', label: '11-50 employees' },
  { value: '51-200', label: '51-200 employees' },
  { value: '201-500', label: '201-500 employees' },
  { value: '501-1000', label: '501-1000 employees' },
  { value: '1000+', label: 'More than 1000 employees' },
];

// Product interest checkboxes
const productInterests = [
  { id: 'resume-analysis', label: 'Resume Analysis' },
  { id: 'interview-plans', label: 'AI Interview Plans' },
  { id: 'job-descriptions', label: 'Job Description Generator' },
  { id: 'enterprise', label: 'Enterprise Solutions' },
];

// Lead sources
const leadSources = [
  { value: 'google', label: 'Google Search' },
  { value: 'social', label: 'Social Media' },
  { value: 'referral', label: 'Referral' },
  { value: 'event', label: 'Event or Conference' },
  { value: 'blog', label: 'Blog or Article' },
  { value: 'other', label: 'Other' },
];

export function ContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Initialize form with strong typing
  const { control, handleSubmit, reset } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      company: '',
      email: '',
      phone: '',
      message: '',
      industry: '',
      companySize: '',
      howHeard: '',
      interest: [],
      urgent: false,
      marketingConsent: false,
    },
  });

  const onSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    
    try {
      // Using FormSubmit.co as a free form submission service
      const response = await fetch('https://formsubmit.co/talent@topmatchtalent.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          ...values,
          _subject: `Contact Form Submission from ${values.firstName} ${values.lastName}`,
        }),
      });
      
      if (response.ok) {
        setIsSubmitted(true);
        reset();
      } else {
        console.error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 px-4 bg-gradient-to-b from-background to-muted/50">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            Get in Touch
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Have questions about TopMatchTalent? Our team is ready to help you find the perfect solution for your hiring needs.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-1">
            <div className="bg-card rounded-xl border border-border shadow-sm p-6 md:p-8 space-y-6">
              <h3 className="text-xl font-semibold mb-4">Contact Information</h3>
              
              <div className="flex items-start space-x-3">
                <Mail className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">Email Us</p>
                  <a href="mailto:talent@topmatchtalent.com" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                    talent@topmatchtalent.com
                  </a>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">Location</p>
                  <p className="text-sm text-muted-foreground">
                    49 Lexington Street<br />
                    London, W1F9AP
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <Clock className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">Business Hours</p>
                  <p className="text-sm text-muted-foreground">
                    Monday-Friday: 9am - 5pm GMT<br />
                    Weekend: Closed
                  </p>
                </div>
              </div>
              
              <div className="pt-4 mt-2">
                <p className="text-sm text-foreground font-medium">Trusted by industry leaders</p>
                <div className="flex flex-wrap gap-4 mt-3">
                  <img src="/images/SnapInc.jpg" alt="Snap Inc" className="h-6 opacity-70" />
                  <img src="/images/Alphabet.png" alt="Alphabet" className="h-6 opacity-70" />
                  <img src="/images/GS.png" alt="Goldman Sachs" className="h-6 opacity-70" />
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="bg-card rounded-xl border border-border shadow-sm p-6 md:p-8">
              {isSubmitted ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="rounded-full bg-primary/10 p-4 mb-4">
                    <CheckCircle className="h-10 w-10 text-primary" />
                  </div>
                  <h3 className="text-2xl font-semibold mb-2">Thank You!</h3>
                  <p className="text-muted-foreground max-w-md mb-6">
                    Your message has been sent successfully. One of our team members will get back to you within 24 hours.
                  </p>
                  <Button 
                    variant="outline" 
                    onClick={() => setIsSubmitted(false)}
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name *</Label>
                      <Controller
                        name="firstName"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <div>
                            <Input id="firstName" placeholder="John" {...field} />
                            {error && <p className="text-sm text-destructive mt-1">{error.message}</p>}
                          </div>
                        )}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Controller
                        name="lastName"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <div>
                            <Input id="lastName" placeholder="Doe" {...field} />
                            {error && <p className="text-sm text-destructive mt-1">{error.message}</p>}
                          </div>
                        )}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Controller
                        name="email"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <div>
                            <Input id="email" type="email" placeholder="your@email.com" {...field} />
                            {error && <p className="text-sm text-destructive mt-1">{error.message}</p>}
                          </div>
                        )}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Controller
                        name="phone"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <div>
                            <Input id="phone" placeholder="+1 123 456 7890" {...field} />
                            {error && <p className="text-sm text-destructive mt-1">{error.message}</p>}
                          </div>
                        )}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="company">Company</Label>
                      <Controller
                        name="company"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <div>
                            <Input id="company" placeholder="Your Company" {...field} />
                            {error && <p className="text-sm text-destructive mt-1">{error.message}</p>}
                          </div>
                        )}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="industry">Industry</Label>
                      <Controller
                        name="industry"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <div>
                            <Select 
                              onValueChange={field.onChange} 
                              defaultValue={field.value}
                            >
                              <SelectTrigger id="industry">
                                <SelectValue placeholder="Select an industry" />
                              </SelectTrigger>
                              <SelectContent>
                                {industries.map((industry) => (
                                  <SelectItem key={industry.value} value={industry.value}>
                                    {industry.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {error && <p className="text-sm text-destructive mt-1">{error.message}</p>}
                          </div>
                        )}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="companySize">Company Size</Label>
                      <Controller
                        name="companySize"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <div>
                            <Select 
                              onValueChange={field.onChange} 
                              defaultValue={field.value}
                            >
                              <SelectTrigger id="companySize">
                                <SelectValue placeholder="Select company size" />
                              </SelectTrigger>
                              <SelectContent>
                                {companySizes.map((size) => (
                                  <SelectItem key={size.value} value={size.value}>
                                    {size.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {error && <p className="text-sm text-destructive mt-1">{error.message}</p>}
                          </div>
                        )}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="howHeard">How did you hear about us?</Label>
                      <Controller
                        name="howHeard"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <div>
                            <Select 
                              onValueChange={field.onChange} 
                              defaultValue={field.value}
                            >
                              <SelectTrigger id="howHeard">
                                <SelectValue placeholder="Select an option" />
                              </SelectTrigger>
                              <SelectContent>
                                {leadSources.map((source) => (
                                  <SelectItem key={source.value} value={source.value}>
                                    {source.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {error && <p className="text-sm text-destructive mt-1">{error.message}</p>}
                          </div>
                        )}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="mb-2">
                      <Label>What are you interested in? (Select all that apply)</Label>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Controller
                        name="interest"
                        control={control}
                        render={({ field }) => (
                          <>
                            {productInterests.map((item) => (
                              <div key={item.id} className="flex items-center space-x-2">
                                <Checkbox
                                  id={item.id}
                                  checked={field.value?.includes(item.id)}
                                  onCheckedChange={(checked: boolean) => {
                                    return checked
                                      ? field.onChange([...field.value || [], item.id])
                                      : field.onChange(
                                          field.value?.filter(
                                            (value) => value !== item.id
                                          )
                                        )
                                  }}
                                />
                                <label
                                  htmlFor={item.id}
                                  className="text-sm font-normal cursor-pointer"
                                >
                                  {item.label}
                                </label>
                              </div>
                            ))}
                          </>
                        )}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Controller
                      name="message"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <div>
                          <Textarea 
                            id="message"
                            placeholder="Tell us how we can help you..." 
                            className="min-h-[120px]" 
                            {...field} 
                          />
                          {error && <p className="text-sm text-destructive mt-1">{error.message}</p>}
                        </div>
                      )}
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="flex items-start space-x-2">
                      <Controller
                        name="urgent"
                        control={control}
                        render={({ field }) => (
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="urgent"
                              checked={field.value}
                              onCheckedChange={(checked: boolean) => field.onChange(checked)}
                            />
                            <label 
                              htmlFor="urgent"
                              className="text-sm font-normal cursor-pointer"
                            >
                              This is an urgent request (we'll respond within 4 hours)
                            </label>
                          </div>
                        )}
                      />
                    </div>
                    
                    <div className="flex items-start space-x-2">
                      <Controller
                        name="marketingConsent"
                        control={control}
                        render={({ field }) => (
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="marketingConsent"
                              checked={field.value}
                              onCheckedChange={(checked: boolean) => field.onChange(checked)}
                            />
                            <label 
                              htmlFor="marketingConsent"
                              className="text-sm font-normal cursor-pointer"
                            >
                              I agree to receive marketing emails from TopMatchTalent
                            </label>
                          </div>
                        )}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-8"
                    >
                      {isSubmitting ? (
                        <>
                          <span className="mr-2">Sending...</span>
                          <span className="animate-spin">
                            <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="mr-2">Send Message</span>
                          <Send className="h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
} 